// src/pages/KarigarManagement/config/karigarMenuConfig.js

import {
  UserGroupIcon,
  BriefcaseIcon,
  CashIcon,
  ClipboardListIcon,
  CogIcon,
  DocumentReportIcon,
  ChartBarIcon,
  PlusCircleIcon,
  ClockIcon,
  CheckCircleIcon,
} from "@heroicons/react/outline";
import {
  FiUsers,
  FiTool,
  FiDollarSign,
  FiActivity,
  FiPlusCircle,
  FiList,
  FiClock,
  FiPieChart,
  FiSettings,
} from "react-icons/fi";
import {
  KARIGAR_ROUTES,
  KARIGAR_PERMISSIONS,
} from "../constants/karigar.constant";

// ========================================
// KARIGAR MENU GROUP
// ========================================
export const KARIGAR_MENU_GROUP = {
  id: "karigar",
  translationKey: "menu.groups.karigar",
  icon: UserGroupIcon,
  color: {
    bg: "bg-amber-500",
    hover: "hover:bg-amber-600",
    light: "bg-amber-50",
    text: "text-amber-600",
    border: "border-amber-500",
  },
  expanded: false,
  featureFlag: "karigarManagement",
  items: [
    // Dashboard
    {
      path: KARIGAR_ROUTES.DASHBOARD,
      translationKey: "menu.items.karigar_dashboard",
      descriptionKey: "menu.descriptions.karigar_dashboard",
      icon: FiActivity,
      permissions: [KARIGAR_PERMISSIONS.VIEW_KARIGARS, "ADMIN"],
      featureFlag: "karigarDashboard",
      badge: { type: "info", count: 0 }, // Active jobs count
    },
    // Karigar List
    {
      path: KARIGAR_ROUTES.KARIGAR_LIST,
      translationKey: "menu.items.karigar_list",
      descriptionKey: "menu.descriptions.karigar_list",
      icon: FiUsers,
      permissions: [KARIGAR_PERMISSIONS.VIEW_KARIGARS, "ADMIN"],
      featureFlag: "karigarList",
    },
    // Add Karigar
    {
      path: KARIGAR_ROUTES.KARIGAR_CREATE,
      translationKey: "menu.items.add_karigar",
      descriptionKey: "menu.descriptions.add_karigar",
      icon: FiPlusCircle,
      permissions: [KARIGAR_PERMISSIONS.CREATE_KARIGAR, "ADMIN"],
      featureFlag: "karigarCreate",
    },
    // Job List
    {
      path: KARIGAR_ROUTES.JOB_LIST,
      translationKey: "menu.items.job_list",
      descriptionKey: "menu.descriptions.job_list",
      icon: FiTool,
      permissions: [KARIGAR_PERMISSIONS.VIEW_JOBS, "ADMIN"],
      featureFlag: "jobList",
      badge: { type: "warning", count: 0 }, // Pending jobs count
    },
    // Create Job
    {
      path: KARIGAR_ROUTES.JOB_CREATE,
      translationKey: "menu.items.create_job",
      descriptionKey: "menu.descriptions.create_job",
      icon: PlusCircleIcon,
      permissions: [KARIGAR_PERMISSIONS.CREATE_JOB, "ADMIN"],
      featureFlag: "jobCreate",
    },
    // Payments
    {
      path: KARIGAR_ROUTES.PAYMENT_LIST,
      translationKey: "menu.items.payments",
      descriptionKey: "menu.descriptions.payments",
      icon: FiDollarSign,
      permissions: [KARIGAR_PERMISSIONS.VIEW_PAYMENTS, "ADMIN"],
      featureFlag: "karigarPayments",
    },
    // Pending Payments
    {
      path: KARIGAR_ROUTES.PAYMENT_PENDING,
      translationKey: "menu.items.pending_payments",
      descriptionKey: "menu.descriptions.pending_payments",
      icon: FiClock,
      permissions: [KARIGAR_PERMISSIONS.VIEW_PAYMENTS, "ADMIN"],
      featureFlag: "pendingPayments",
      badge: { type: "error", count: 0 }, // Pending payments count
    },

    // Future menu items (ready to uncomment when screens are built)
    // {
    //   path: KARIGAR_ROUTES.REPORTS,
    //   translationKey: "menu.items.karigar_reports",
    //   descriptionKey: "menu.descriptions.karigar_reports",
    //   icon: DocumentReportIcon,
    //   permissions: [KARIGAR_PERMISSIONS.VIEW_KARIGAR_REPORTS, "ADMIN"],
    //   featureFlag: "karigarReports",
    // },
    // {
    //   path: KARIGAR_ROUTES.ANALYTICS,
    //   translationKey: "menu.items.karigar_analytics",
    //   descriptionKey: "menu.descriptions.karigar_analytics",
    //   icon: FiPieChart,
    //   permissions: [KARIGAR_PERMISSIONS.VIEW_KARIGAR_ANALYTICS, "ADMIN"],
    //   featureFlag: "karigarAnalytics",
    // },
    // {
    //   path: KARIGAR_ROUTES.SETTINGS,
    //   translationKey: "menu.items.karigar_settings",
    //   descriptionKey: "menu.descriptions.karigar_settings",
    //   icon: FiSettings,
    //   permissions: [KARIGAR_PERMISSIONS.MANAGE_KARIGAR_SETTINGS, "ADMIN"],
    //   featureFlag: "karigarSettings",
    // },
  ],
};

// ========================================
// KARIGAR SUB-MENU GROUPS (for nested navigation)
// ========================================
export const KARIGAR_SUB_MENUS = {
  karigars: {
    id: "karigars-submenu",
    translationKey: "menu.subgroups.karigars",
    icon: FiUsers,
    items: [
      {
        path: KARIGAR_ROUTES.KARIGAR_LIST,
        translationKey: "menu.items.all_karigars",
        icon: FiList,
        permissions: [KARIGAR_PERMISSIONS.VIEW_KARIGARS],
      },
      {
        path: KARIGAR_ROUTES.KARIGAR_CREATE,
        translationKey: "menu.items.add_karigar",
        icon: FiPlusCircle,
        permissions: [KARIGAR_PERMISSIONS.CREATE_KARIGAR],
      },
    ],
  },
  jobs: {
    id: "jobs-submenu",
    translationKey: "menu.subgroups.jobs",
    icon: FiTool,
    items: [
      {
        path: KARIGAR_ROUTES.JOB_LIST,
        translationKey: "menu.items.all_jobs",
        icon: FiList,
        permissions: [KARIGAR_PERMISSIONS.VIEW_JOBS],
      },
      {
        path: KARIGAR_ROUTES.JOB_CREATE,
        translationKey: "menu.items.create_job",
        icon: FiPlusCircle,
        permissions: [KARIGAR_PERMISSIONS.CREATE_JOB],
      },
    ],
  },
  payments: {
    id: "payments-submenu",
    translationKey: "menu.subgroups.payments",
    icon: FiDollarSign,
    items: [
      {
        path: KARIGAR_ROUTES.PAYMENT_LIST,
        translationKey: "menu.items.all_payments",
        icon: FiList,
        permissions: [KARIGAR_PERMISSIONS.VIEW_PAYMENTS],
      },
      {
        path: KARIGAR_ROUTES.PAYMENT_PENDING,
        translationKey: "menu.items.pending_payments",
        icon: FiClock,
        permissions: [KARIGAR_PERMISSIONS.VIEW_PAYMENTS],
      },
    ],
  },
};

// ========================================
// QUICK ACCESS ITEMS FOR KARIGAR
// ========================================
export const KARIGAR_QUICK_ACCESS = [
  //   {
  //     path: KARIGAR_ROUTES.JOB_CREATE,
  //     translationKey: "menu.quick_access.create_job",
  //     icon: PlusCircleIcon,
  //     color: "bg-gradient-to-r from-amber-500 to-orange-600",
  //     permissions: [KARIGAR_PERMISSIONS.CREATE_JOB],
  //     isSpecial: true,
  //     featureFlag: "quickAccessCreateJob",
  //   },
  //   {
  //     path: KARIGAR_ROUTES.PAYMENT_PENDING,
  //     translationKey: "menu.quick_access.pending_payments",
  //     icon: FiClock,
  //     color: "bg-gradient-to-r from-red-500 to-pink-600",
  //     permissions: [KARIGAR_PERMISSIONS.VIEW_PAYMENTS],
  //     isSpecial: true,
  //     featureFlag: "quickAccessPendingPayments",
  //     badge: { type: "error", dynamic: true },
  //   },
  // {
  //   path: KARIGAR_ROUTES.KARIGAR_CREATE,
  //   translationKey: "menu.quick_access.add_karigar",
  //   icon: FiUsers,
  //   color: "bg-gradient-to-r from-blue-500 to-indigo-600",
  //   permissions: [KARIGAR_PERMISSIONS.CREATE_KARIGAR],
  //   isSpecial: true,
  //   featureFlag: "quickAccessAddKarigar",
  // },
];

// ========================================
// KARIGAR FEATURE FLAGS
// ========================================
export const KARIGAR_FEATURE_FLAGS = {
  // Main module
  karigarManagement: true,
  karigarDashboard: true,

  // Karigar features
  karigarList: true,
  karigarCreate: true,
  karigarEdit: true,
  karigarDetail: true,
  karigarAvailability: true,

  // Job features
  jobList: true,
  jobCreate: true,
  jobEdit: true,
  jobDetail: true,
  jobAssign: true,

  // Payment features
  karigarPayments: true,
  pendingPayments: true,
  karigarEarnings: true,

  // Quick access
  quickAccessCreateJob: true,
  quickAccessPendingPayments: true,
  quickAccessAddKarigar: false,

  // Future features (set to false until implemented)
  karigarReports: false,
  karigarAnalytics: false,
  karigarSettings: false,
};

// ========================================
// BREADCRUMB CONFIG
// ========================================
export const KARIGAR_BREADCRUMBS = {
  [KARIGAR_ROUTES.DASHBOARD]: [
    { translationKey: "breadcrumbs.karigar", path: KARIGAR_ROUTES.DASHBOARD },
  ],
  [KARIGAR_ROUTES.KARIGAR_LIST]: [
    { translationKey: "breadcrumbs.karigar", path: KARIGAR_ROUTES.DASHBOARD },
    {
      translationKey: "breadcrumbs.karigars",
      path: KARIGAR_ROUTES.KARIGAR_LIST,
    },
  ],
  [KARIGAR_ROUTES.KARIGAR_CREATE]: [
    { translationKey: "breadcrumbs.karigar", path: KARIGAR_ROUTES.DASHBOARD },
    {
      translationKey: "breadcrumbs.karigars",
      path: KARIGAR_ROUTES.KARIGAR_LIST,
    },
    { translationKey: "breadcrumbs.add_karigar", path: null },
  ],
  [KARIGAR_ROUTES.JOB_LIST]: [
    { translationKey: "breadcrumbs.karigar", path: KARIGAR_ROUTES.DASHBOARD },
    { translationKey: "breadcrumbs.jobs", path: KARIGAR_ROUTES.JOB_LIST },
  ],
  [KARIGAR_ROUTES.JOB_CREATE]: [
    { translationKey: "breadcrumbs.karigar", path: KARIGAR_ROUTES.DASHBOARD },
    { translationKey: "breadcrumbs.jobs", path: KARIGAR_ROUTES.JOB_LIST },
    { translationKey: "breadcrumbs.create_job", path: null },
  ],
  [KARIGAR_ROUTES.PAYMENT_LIST]: [
    { translationKey: "breadcrumbs.karigar", path: KARIGAR_ROUTES.DASHBOARD },
    {
      translationKey: "breadcrumbs.payments",
      path: KARIGAR_ROUTES.PAYMENT_LIST,
    },
  ],
  [KARIGAR_ROUTES.PAYMENT_PENDING]: [
    { translationKey: "breadcrumbs.karigar", path: KARIGAR_ROUTES.DASHBOARD },
    {
      translationKey: "breadcrumbs.payments",
      path: KARIGAR_ROUTES.PAYMENT_LIST,
    },
    { translationKey: "breadcrumbs.pending", path: null },
  ],
};

// ========================================
// DASHBOARD STAT CARDS CONFIG
// ========================================
export const KARIGAR_DASHBOARD_STATS = [
  {
    id: "total_karigars",
    translationKey: "dashboard.stats.total_karigars",
    icon: FiUsers,
    color: "blue",
    valueKey: "totalKarigars",
    trend: true,
  },
  {
    id: "active_jobs",
    translationKey: "dashboard.stats.active_jobs",
    icon: FiTool,
    color: "amber",
    valueKey: "activeJobs",
    trend: true,
  },
  {
    id: "pending_payments",
    translationKey: "dashboard.stats.pending_payments",
    icon: FiClock,
    color: "red",
    valueKey: "pendingPayments",
    trend: true,
  },
  {
    id: "completed_this_month",
    translationKey: "dashboard.stats.completed_this_month",
    icon: CheckCircleIcon,
    color: "green",
    valueKey: "completedThisMonth",
    trend: true,
  },
];
